import {
  Box,
  FormControlLabel,
  Grid,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import PatientDetailsHeader from '../../../components/Account/PatientDetailsHeader';
import BackButton from '../../../components/BackButton';
import { ExportButton, PrintableSection } from '../../../components/Print';
import {
  getCompliancePeriods,
  togglePatientComplianceAutotracking,
} from '../../../redux/actions/complianceActions';
import { getPatientDetail } from '../../../redux/actions/healthcareProfessionalActions';
import { useAppThunkDispatch } from '../../../redux/configureStore';
import { selectedPatientDetail } from '../../../redux/selectors/patientSelectors';
import ComplianceDetailTableComponent from './ComplianceDetailTable';

const ComplianceDetail: React.FC = () => {
  const { t } = useTranslation('compliance');
  const { patientId } = useParams();
  const patientDetails = useSelector(selectedPatientDetail);
  const dispatch = useAppThunkDispatch();

  const toggleSetAutotrack = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(
      togglePatientComplianceAutotracking(patientId!, event.target.checked)
    );
  };

  useEffect(() => {
    if (patientId) {
      dispatch(getCompliancePeriods(patientId));
      if (!patientDetails) {
        dispatch(getPatientDetail(patientId));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <PatientDetailsHeader selectedPatientId={patientId} />
      <BackButton />

      <Grid
        container
        sx={{ alignItems: 'center', justifyContent: 'space-between',  mt: 1, pr: 2 }}
      >
        <Grid>
          <Typography variant="h5">
            <strong>{t('title')}</strong>
          </Typography>
        </Grid>
        <Grid sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {patientDetails ? (
            <Tooltip title={t('autoTracking.tooltip')}>
              <FormControlLabel
                control={
                  <Switch
                    data-testid="switch-auto-tracking"
                    checked={patientDetails.isComplianceEnabled}
                    onChange={toggleSetAutotrack}
                    color="primary"
                  />
                }
                label={t('autoTracking.label')}
                labelPlacement="end"
              />
            </Tooltip>
          ) : (
            <Typography sx={{ mr: 2 }}>
              {t('autoTracking.cannotConfigure')}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Grid
        container
        spacing={2}
       sx={{ mt: 1, alignItems: 'stretch',  flexDirection: 'column' }}>
        <Grid sx={{ mr: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <ExportButton
              targetId="compliance-table-printable"
              variant="contained"
              label={t('export.label')}
              tooltip={t('export.tooltip')}
              filename="compliance-report.pdf"
            />
          </Box>
          <PrintableSection
            id="compliance-table-printable"
            title={t('report.title')}
            printHeader={
              <Box sx={{ marginTop: 1 }}>
                <Typography variant="body2">
                  {t('report.patient', {
                    name: `${patientDetails?.firstName ?? ''} ${
                      patientDetails?.lastName ?? ''
                    }`.trim(),
                  })}
                </Typography>
                <Typography variant="body2">
                  {t('report.generated', {
                    date: new Date().toLocaleDateString(),
                  })}
                </Typography>
              </Box>
            }
            printFooter={
              <Typography variant="caption">{t('report.footer')}</Typography>
            }
          >
            <ComplianceDetailTableComponent />
          </PrintableSection>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ComplianceDetail;
